import type { ReactNode } from 'react';

export type CardVariant = 'surface-1' | 'surface-2' | 'elevated';

export interface CardProps {
  readonly children: ReactNode;
  readonly variant?: CardVariant;
  readonly className?: string;
  readonly onClick?: () => void;
  readonly interactive?: boolean;
}

/**
 * Surface card primitive.
 *
 * Variants:
 * - surface-1: light list/summary surface (default)
 * - surface-2: hero/elevated surface
 * - elevated: surface-2 with a soft shadow
 */
export function Card({
  children,
  variant = 'surface-1',
  className = '',
  onClick,
  interactive = false,
}: CardProps) {
  const variantClasses: Record<CardVariant, string> = {
    'surface-1': 'rounded-xl bg-surface-1 p-4',
    'surface-2': 'rounded-xl bg-surface-2 p-4',
    elevated: 'rounded-xl bg-surface-2 p-4 shadow-md',
  };
  const interactiveClasses = interactive
    ? 'transition-colors hover:bg-surface-2 cursor-pointer active:scale-[0.99]'
    : '';
  return (
    <div
      className={[variantClasses[variant], interactiveClasses, className]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      {children}
    </div>
  );
}
